// @ds/config/schema — schema primitives for entity field definitions.
//
// Four utilities:
//   - `defineSchema<T>(meta)(fields, opts?)`
//       Curried factory. `meta: SchemaMeta` declares entity-intrinsic
//       identity (resource, writeResource, entityType, labels, softDelete,
//       permanentFields, invalidateResources). Each property key in
//       `fields` becomes the field's `key` automatically. With
//       `{ audit: true }`, merges `created_at`/`updated_at` audit fields.
//
//
//   - `pick(schema, keys)`
//       Selects a subset of schema fields preserving the per-entry generic
//       `P`. Each entry is either a string key or `{ key, ...overrides }`
//       for per-pick customization. Type-safe — typos surface as TS errors;
//       unknown keys throw at runtime.
//
//   - `pickColumns(schema, keys)`
//       Same as `pick` but the per-entry override slot accepts
//       `ListColumnSpec` knobs (width, pinned, defaultVisible, etc.).
//       This is the canonical entry point for LIST PAGE columns —
//       placement decisions live at the consumer's pick site, not in the
//       schema.
//
//   - `liftField` / `ref`
//       Cross-entity field reuse — lift a relation field from entity A
//       onto entity B by traversing a typed relation. `ref()` is the sugar
//       over the well-known entity-chip pattern via `entityRefRegistry`.
//
// Design principle: schemas describe FIELDS (field-intrinsic shape — what
// the field IS). List pages decide PLACEMENT at the consumer site (widths,
// pinning, visibility — what the field LOOKS LIKE on this particular page).

import type { FieldDef } from "@ds/display/fieldDisplay";
import type { EditFieldSpec } from "@ds/editable/editFieldSpec";
import type { ListColumnSpec, FilterDef } from "@ds/tables/types";
import type { BoxWidth } from "@ds/tables/Table";
import type { TFunction } from "i18next";
import { AUDIT_FIELDS, type AuditFieldKey } from "@ds/data/auditFields";

// ─── Helper: non-scalar keys on T (relations) ────────────────────────────
//
// Used in SchemaField.deps.relations to restrict which keys can be
// referenced as relation keys. Scalar = string | number | boolean | Date
// (incl. null/undefined wrappers). Arrays of scalars also excluded. Object
// types and object arrays → relation key.

type IsScalar<V> = NonNullable<V> extends string | number | boolean | Date
  ? true
  : NonNullable<V> extends Array<string | number | boolean | Date>
    ? true
    : false;

type RelationKeysOf<T> = {
  [K in keyof T]: IsScalar<T[K]> extends true ? never : K;
}[keyof T];

// For an array relation `corrections: Row[] | null | undefined`, unwrap the
// element type so `keyof` returns row keys (`"id" | "invoice_number" | ...`)
// instead of Array prototype keys (`"length" | "push" | ...`).
type UnwrapRelation<V> =
  NonNullable<V> extends ReadonlyArray<infer U> ? NonNullable<U> : NonNullable<V>;

// ─── SchemaField — typed field declaration for entity T ─────────────────
//
// Re-bases `FieldDef<T, P>` with T-bound `deps`, `data`, and `edit` so
// `deps.fields: ["nipq"]` (typo) is a TS error.
//
// `key` is injected by `defineSchema` from the record key — authors omit it.

// `SchemaField` is the field-intrinsic spec authored on entity schemas.
//
// What's on it: everything `FieldDef` carries (component / props / data /
// render / labelKey / sortable / filterable / exportAs / subQuery / readOnly /
// defaultAlign / edit) PLUS T-bound `deps` (relation keys typo-check against
// `keyof T`) and T-bound `edit.key`.
//
// What's NOT on it: list-page placement knobs (width, pinned, defaultVisible,
// hideable, fixed, minWidth, ellipsis, align, sorter, onCell, access). Those
// live ONLY at `pickColumns()` sites — schemas don't know which list pages
// their fields appear on, so they don't declare placement.
//
// Placement-on-schema declarations are TS errors by design (the entity
// migration moves them to `config.list.columns = pickColumns(schema, [{key:
// "name", width: 200, pinned: "left"}])`).
export type SchemaField<T, P, E = unknown> =
  Omit<FieldDef<T, P>, "key" | "deps" | "data" | "edit" | "onCommit" | "onClick" | "onUpload"> & {
    /**
     * Inline-commit handler — narrowed to entity's extras context `E`.
     * Bound by `renderCell` to `(record, extras)` before reaching the
     * primitive's `onCommit: (next) => void` prop.
     */
    onCommit?: (record: T, next: unknown, extras: E) => void | Promise<void>;
    /** Click handler — narrowed to entity's extras context `E`. */
    onClick?: (record: T, extras: E) => void | Promise<void>;
    /** Upload handler — narrowed to entity's extras context `E`. */
    onUpload?: (record: T, files: FileList, extras: E) => void | Promise<void>;
    /**
     * Data extractor — maps the entity record to the component's data props.
     * When the field declares `subQuery: {...}`, the renderer passes the
     * matched sub-query rows as the second arg (otherwise `undefined`).
     */
    data?: (record: T, subRows?: ReadonlyArray<unknown>) => Partial<P>;
    /**
     * Dependency declaration — tells the Supabase select-string builder
     * which fields and relations this field needs. Typed against T's
     * actual keys to prevent drift.
     *
     * Note: when the field declares `subQuery: {...}`, the subQuery's FK
     * column is auto-extended into `deps.foreignKeys` by the harvester —
     * no need to also list it here.
     */
    deps?: {
      /** Scalar fields on T needed to render this field. */
      fields?: ReadonlyArray<Extract<keyof T, string>>;
      /**
       * Related object fields. Key = relation property on T (must be
       * object-shaped). Value = fields to select from the related entity.
       */
      relations?: {
        [K in Extract<RelationKeysOf<T>, string>]?:
          ReadonlyArray<Extract<keyof UnwrapRelation<T[K]>, string>>;
      };
      /** Foreign key columns needed to build the PostgREST relation path. */
      foreignKeys?: {
        [K in Extract<keyof T, string>]?: {
          foreignKey: string;
          /** Target-table pk seeded into the embed (default `id`). */
          pk?: string;
          nested?: Record<string, string>;
        };
      };
      /** Deep-nested relations (rarely needed; e.g. parent.parent). */
      nested?: Record<string, Record<string, ReadonlyArray<string>>>;
    };
    /**
     * Inline-edit metadata. Same shape as `FieldDef.edit` but `key` is
     * T-bound. `field` / `value` / `readOnly` / `required` accept either
     * a static value or a function of the record.
     */
    edit?: {
      key: Extract<keyof T, string>;
      /**
       * Editor type — `EditFieldSpec` (discriminated union) or a function
       * of the record (for per-record options like `displayLabel` on
       * entity pickers). Defaults to `{ type: "text" }` when omitted.
       */
      field?: EditFieldSpec | ((record: T) => EditFieldSpec);
      value?: unknown | ((record: T) => unknown);
      readOnly?: boolean | ((record: T) => boolean);
      required?: boolean | ((record: T) => boolean);
      validate?: (v: unknown) => string | null;
    };
    /**
     * Marks this field as participating in the entity's free-text search.
     * Consumed by `ListConfig.searchEntity` derivation in Pass E — the
     * Query dispatcher will OR-filter across every `searchable: true` field
     * when a search term is present.
     *
     * Default: false. Explicit `false` is equivalent to omission.
     */
    searchable?: boolean;
  };

/** The fetch-dependency declaration carried by any field. `FieldDef`'s `deps`
 *  is independent of both its type parameters, so no erasure is needed. */
export type FieldDeps = FieldDef["deps"];
type FieldDepsObj = NonNullable<FieldDeps>;

/**
 * A `SchemaField` whose component prop type is erased. The `any` is
 * irreducible: each field carries its own component prop type `P`, and TS has
 * no existential to quantify those in one object type (same reasoning as
 * `PresentationFieldMap` in `@ds/config/presentation`). Declared once here so
 * consumers reference it instead of repeating `any` per site.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- same contravariant component-props slot as ErasedTypeArg (fieldDisplay.tsx): SchemaField's middle type arg reaches a ComponentType<P>, which `unknown` breaks. Declared once here rather than `any` (plus a disable) at each buildFields/defineSchema signature.
export type AnySchemaField<T, E = unknown> = SchemaField<T, any, E>;

// ─── SchemaMeta — entity-intrinsic identity ──────────────────────────────
//
// Captures the data-model concerns that today are scattered across
// `EntityConfig.resource/writeResource/invalidateResources/entityType/labels`.
// Owned by the schema so every Query<T>, dispatcher and downstream surface
// reads from one source of truth (no redeclaration).

export interface SchemaMeta {
  /** Refine resource the rows are READ from. Required. */
  resource: string;
  /** Override when reading from a view but WRITING to the underlying table. */
  writeResource?: string;
  /**
   * Resources to invalidate after mutations against this schema. The
   * dispatcher auto-includes `resource` + `writeResource`; declare extras
   * (related views, summary tables) here.
   */
  invalidateResources?: readonly string[];
  /** Entity kind discriminator — used by cross-entity drawers, comment routing, audit-log grouping. */
  entityType: string;
  /**
   * Primary-key column when the resource's PK is not `id` (e.g.
   * `app_configuration`, pk `key`). The shells derive everything from it:
   * the list select aliases it to a runtime `id` (`select=id:key,key,…` —
   * the same synthesized-id contract view-backed lists get in SQL), the
   * default-sort tiebreaker sorts by it instead of `id`, and SchemaForm
   * addresses getOne/update with it via Refine's `meta.idColumnName`.
   * Default `"id"`.
   */
  idColumn?: string;
  /**
   * Apply the canonical `is_deleted=false` permanent filter to every
   * `Query<T>` against this schema. Default false.
   */
  softDelete?: boolean;
  /** Localized labels — read by breadcrumbs, modal titles, empty states. */
  labels: {
    singular: (t: TFunction) => string;
    plural:   (t: TFunction) => string;
  };
  /**
   * Fields that must always round-trip in the fetch, even when no display
   * references them (e.g. `id`, `currency`, tone-driving status columns).
   */
  permanentFields?: readonly string[];
}

// ─── Schema<T> — schema instance with attached meta ──────────────────────
//
// The factory attaches `__meta` via a non-enumerable property so it doesn't
// pollute `Object.keys(schema)` (which iterates only the declared fields).
// Downstream code reads it via `getSchemaMeta(schema)`.

// Index signature uses `unknown` so concrete `SchemaField<T, P>` entries
// remain assignable; `__meta` is attached as a non-enumerable property and
// callers reach it via `getSchemaMeta(schema)` rather than dotted access.
export type Schema<T> = {
  readonly [K: string]: SchemaField<T, unknown>;
};

/**
 * Reads the `SchemaMeta` attached to a `Schema<T>`. Throws when called on a
 * schema that was never given meta.
 */
export function getSchemaMeta<T>(schema: Schema<T> | Record<string, SchemaField<T, unknown>>): SchemaMeta {
  const meta = (schema as { __meta?: SchemaMeta }).__meta;
  if (!meta) {
    throw new Error(
      "schema missing __meta — was it created with defineSchema(meta)(fields)?",
    );
  }
  return meta;
}

// ─── defineSchema ──────────────────────────────────────────────────────────

/** Options for `defineSchema<T>(meta)(fields, opts?)`. */
export interface DefineSchemaOptions {
  /**
   * When true, merges the canonical `created_at` and `updated_at` audit
   * fields from `@ds/data/auditFields` into the schema. Both can still
   * be overridden by declaring them explicitly in the `fields` argument.
   */
  audit?: boolean;
}

// Curried factory: T is explicit, F is inferred from the fields argument.
// Each field's `key` is injected from the record key. Audit fields are
// merged when `opts.audit` is true (with caller fields winning on conflict).

// Curried factory: T is explicit, F is inferred from the fields argument.
//
// The audit field merge is a runtime concern; the return type always
// includes `created_at` / `updated_at` as optional indexable string keys
// so consumers can `pick(schema, ["created_at"])` whether or not opts
// requested them. Runtime throws on `pick` with an unknown key.

// `E` is the entity's extras-context type. When omitted, defaults to `unknown`.
// Pass an explicit second arg (`defineSchema<Transaction, TxExtras>(meta)`) to
// narrow `onCommit/onClick/onUpload`'s `extras` parameter from `unknown` to
// the entity-specific context type.

// Build-fields helper shared by both signatures. Returns a plain object of
// keyed fields; the caller decides whether to attach `__meta`.
function buildFields<T, E, F extends Record<string, AnySchemaField<T, E>>>(
  fields: F,
  opts?: DefineSchemaOptions,
): Record<string, AnySchemaField<T, E> & { key: string }> {
  const out: Record<string, AnySchemaField<T, E> & { key: string }> = {};

  // Merge audit fields FIRST so caller-supplied fields can override.
  if (opts?.audit) {
    for (const [key, field] of Object.entries(AUDIT_FIELDS)) {
      out[key] = { ...(field as unknown as AnySchemaField<T, E>), key };
    }
  }

  for (const key in fields) {
    out[key] = { ...fields[key], key };
  }
  return out;
}

function attachMeta<O extends Record<string, unknown>>(out: O, meta: SchemaMeta): O {
  Object.defineProperty(out, "__meta", {
    value: meta,
    enumerable: false,
    configurable: false,
    writable: false,
  });
  return out;
}

/**
 * Meta-carrying schema factory. Returns a curried fields factory; `__meta` is
 * attached at runtime as a non-enumerable property (so `Object.keys` lists
 * only fields, and `Record<string, FieldDef<...>>` consumers stay happy).
 * Callers read it via `getSchemaMeta(schema)`.
 */
export function defineSchema<T, E = unknown>(meta: SchemaMeta) {
  return function <F extends Record<string, AnySchemaField<T, E>>>(
    fields: F,
    opts?: DefineSchemaOptions,
  ): BuiltSchema<T, E, F> {
    const out = buildFields<T, E, F>(fields, opts);
    attachMeta(out, meta);
    return out as unknown as BuiltSchema<T, E, F>;
  };
}

/** The keyed result of `defineSchema(meta)(fields)`: every authored field with
 *  its `key` injected, plus the always-present audit keys. */
type BuiltSchema<T, E, F> = {
  readonly [K in keyof F]: AnySchemaField<T, E> & { key: K & string };
} & Record<AuditFieldKey, AnySchemaField<T, E> & { key: string }>;


// ─── pickColumns — list-page columns with placement overrides ───────────
//
// Canonical entry point for LIST PAGE columns. The per-pick override slot
// accepts `ListColumnSpec` placement knobs (width, pinned, defaultVisible,
// hideable, align, sorter, fixed, ellipsis, minWidth, onCell, access).
//
// Schema authors stop putting these on `SchemaField` directly; list pages
// declare them at the pick site where the placement decision actually lives.

// Every prop-typed key (`component`/`data`/`props`/`render`/…) is omitted, so
// the erased `unknown` prop parameter never surfaces here.
type ListColumnOverride<T> = Partial<Omit<ListColumnSpec<T, unknown>, "key" | "component" | "data" | "props" | "render" | "deps" | "subQuery" | "edit">>;

/**
 * Object-ref placement: attach presentation overrides to a field object — one
 * helper for table columns AND row-card slots. The layout reads what it needs
 * (table → width/pinned/align/…, row-card → position).
 *
 *   col(edprSchema.year, { width: 90 })            // table column
 *   col(contactSchema.full_name, { position: "primary" })  // row-card slot
 *   col(schema.x)                                  // plain field (kv/cards)
 */
export function col<T, P>(
  field: FieldDef<T, P>,
  overrides: { position: import("@ds/config/displayLayout").SlotPosition } & Partial<FieldDef<T, P>>,
): import("@ds/config/displayLayout").SlotSpec<T>;
export function col<T, P>(
  field: FieldDef<T, P>,
  overrides?: ListColumnOverride<T>,
): ListColumnSpec<T, P>;
export function col<T, P>(
  field: FieldDef<T, P>,
  overrides:
    | ListColumnOverride<T>
    | ({ position: import("@ds/config/displayLayout").SlotPosition } & Partial<FieldDef<T, P>>) = {},
): ListColumnSpec<T, P> | import("@ds/config/displayLayout").SlotSpec<T> {
  return { ...field, ...overrides } as ListColumnSpec<T, P>;
}

/**
 * Deps for reading `columns` across a single join — the deps half of `pickRef`,
 * for composite fields that read more than one relation (e.g. a route reading
 * load + unload companies). Pair with `composeDeps`.
 */
export function refDeps<T>(
  join: JoinSpec<T>,
  columns: ReadonlyArray<string>,
): FieldDeps {
  return foldHopDeps(String(join.relation), join.fk, join.also ?? [], {
    fields: [...columns],
  });
}

/** Merge several `deps` (union fields/relations/nested, last-wins foreignKeys). */
export function composeDeps<T = unknown>(...parts: Array<FieldDeps>): FieldDef<T>["deps"] {
  const fields = new Set<string>();
  const relations: Record<string, string[]> = {};
  const nested: Record<string, Record<string, ReadonlyArray<string>>> = {};
  const foreignKeys: NonNullable<FieldDepsObj["foreignKeys"]> = {};
  for (const p of parts) {
    if (!p) continue;
    p.fields?.forEach((f) => fields.add(f));
    if (p.relations) for (const [k, v] of Object.entries(p.relations)) {
      relations[k] = Array.from(new Set([...(relations[k] ?? []), ...v]));
    }
    if (p.nested) for (const [k, v] of Object.entries(p.nested)) {
      nested[k] = { ...(nested[k] ?? {}), ...(v as Record<string, string[]>) };
    }
    if (p.foreignKeys) for (const [k, v] of Object.entries(p.foreignKeys)) {
      foreignKeys[k] = v;
    }
  }
  const d: FieldDepsObj = {};
  if (fields.size) d.fields = [...fields];
  if (Object.keys(relations).length) d.relations = relations;
  if (Object.keys(nested).length) d.nested = nested;
  if (Object.keys(foreignKeys).length) d.foreignKeys = foreignKeys;
  return d;
}

/**
 * Object-ref sort entry: sort by a schema field. The Postgres sort column is
 * the field's `key` (== the column for scalar/FK fields). For a raw column
 * with no field (e.g. `id`), pass `{ field: "id", order }` directly.
 */
export function sortBy(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  field: FieldDef<any, any>,
  order: "asc" | "desc",
): { field: string; order: "asc" | "desc" } {
  if (!field.key) throw new Error("[schema.sortBy] field has no key");
  return { field: field.key, order };
}



// ─── defineJoins + pickRef ──────────────────────────────────────────────────
//
// A schema declares its relationships ONCE (`defineJoins`); any target-schema
// field is then projected across a named join with `pickRef(join, field)`,
// which derives `data` + `deps` (relations / nested / FK aliases) from the
// join + the field. No per-field FK/alias/nested boilerplate.
//
// Joins are SINGLE-HOP. Cross-schema reuse composes: a field that is itself a
// `pickRef` result (e.g. `edprRequestSchema.applicant_card`) can be lifted
// again across another hop — `pickRef` folds its deps one level deeper, so
// multi-level joins emerge from composition, declared where each hop belongs.

/** A single-hop relationship on `T`: the relation property + optional non-default FK + text fallback. */
export interface JoinSpec<T> {
  /** Relation property on `T` (must be object-shaped). */
  relation: Extract<RelationKeysOf<T>, string>;
  /** FK column on `T`. Omit when it's the default `${relation}_id`. */
  fk?: string;
  /** Extra scalar columns on `T` to fetch (e.g. a `fallback`'s text source). */
  also?: ReadonlyArray<string>;
  /** Coalesce a target sub-record when the FK didn't resolve (e.g. a scraped text name). */
  fallback?: (record: T) => unknown;
  /**
   * Entity-select kind for editing the FK. When set, `pickRef` emits an
   * `edit: { key: <scalar fk>, field: { type: "entity-select", kind } }` so the
   * same field is both the display chip and the form picker. Omit for read-only.
   */
  entity?: string;
}

/** Declare a schema's joins once. `defineJoins<T>()({ name: { relation, … } })`.
 *  Each returned join is typed as `JoinSpec<T>` (not the narrow literal) so a
 *  later `pickRef(joins.x, …)` correctly infers `TParent = T` rather than
 *  re-deriving a minimal `{ <relation>: any }` shape from the literal. */
export function defineJoins<T>() {
  return function <J extends Record<string, JoinSpec<T>>>(
    joins: J,
  ): { readonly [K in keyof J]: JoinSpec<T> } {
    return joins;
  };
}

// Fold a source field's deps one level under `rel`, wiring FK + alias hints.
function foldHopDeps(
  rel: string,
  fk: string | undefined,
  also: ReadonlyArray<string>,
  sDeps: FieldDeps,
): FieldDeps {
  // `fk` may be a PostgREST table hint (`companies!load_commercial_company_id`);
  // the scalar column to fetch is the part after `!`, the embed hint is the whole.
  const scalarFk = fk?.includes("!") ? fk.split("!")[1] : fk;
  const d: FieldDepsObj = {
    fields: [...(scalarFk ? [scalarFk] : []), ...also],
    relations: { [rel]: [...(sDeps?.fields ?? [])] },
  };
  // The source's own relations + deeper nested become nested embeds under `rel`.
  const nested: Record<string, unknown> = { ...(sDeps?.relations ?? {}) };
  if (sDeps?.nested) {
    for (const [k, v] of Object.entries(sDeps.nested)) {
      nested[k] = { ...((nested[k] as object) ?? {}), ...v };
    }
  }
  // The accumulator mixes relation column lists with deeper nested maps, so it
  // is widened to `unknown` and re-asserted at the one assignment site.
  if (Object.keys(nested).length > 0) {
    d.nested = { [rel]: nested } as FieldDepsObj["nested"];
  }
  // The source's FK aliases become nested FK hints under `rel`.
  const nestedFks: Record<string, string> = {};
  if (sDeps?.foreignKeys) {
    for (const [srcRel, meta] of Object.entries(sDeps.foreignKeys)) {
      if (meta?.foreignKey) nestedFks[srcRel] = meta.foreignKey;
      if (meta?.nested) Object.assign(nestedFks, meta.nested);
    }
  }
  if (fk || Object.keys(nestedFks).length > 0) {
    // `foreignKey` is spread conditionally (a nested-only hint carries none),
    // so the literal is re-asserted against the deps shape.
    d.foreignKeys = {
      [rel]: {
        ...(fk ? { foreignKey: fk } : {}),
        ...(Object.keys(nestedFks).length > 0 ? { nested: nestedFks } : {}),
      },
    } as FieldDepsObj["foreignKeys"];
  }
  return d;
}

/**
 * Project a target-schema field across a single-hop join. `data` reads
 * `record[relation]` (or the join's `fallback`); `deps` is derived from the
 * join + the field's own deps. The output field's `key` is injected by
 * `defineSchema` from the property it's assigned to.
 */
/** Reserved (non-prop) knobs accepted by `pickRef` opts. Everything else in
 *  `opts` is treated as a `Partial<P>` prop override for the target component. */
export interface PickRefOpts {
  /** Output column key. Defaults to the target field's key. */
  key?: string;
  /** i18n label key. Defaults to the target field's labelKey. */
  labelKey?: string;
  /** Display width (placement). */
  width?: BoxWidth | number | string;
  /** Default-visible hint for list pages. */
  defaultVisible?: boolean;
  /** Sortable column. */
  sortable?: boolean;
  /** Placeholder shown in the entity-select edit widget (needs `join.entity`). */
  placeholder?: string;
  /** Mark the FK edit required (needs `join.entity`). */
  required?: boolean;
  /** EntitySelect edit filters (category / role / requireNip / …). */
  filter?: { category?: string; role?: string; requireNip?: boolean; [k: string]: unknown };
  /** List/section filter declaration lifted by `deriveFilters`. */
  filterable?: FilterDef;
}

const PICK_REF_RESERVED = new Set([
  "key", "labelKey", "width", "defaultVisible", "sortable", "placeholder", "required", "filter", "filterable",
]);

export function pickRef<TParent, P>(
  join: JoinSpec<TParent>,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  field: FieldDef<any, P>,
  /** Field key + label + placement + edit knobs, plus any prop overrides for the target component. */
  opts?: PickRefOpts & Partial<P>,
): ListColumnSpec<TParent, P> {
  // Returns ListColumnSpec (FieldDef + required key) so the result drops into
  // table `columns`, kv-grid/cards `fields`, a form `{ field }` entry —
  // anywhere. Relation fields live in the CONFIG (columns + form), not the
  // schema, so no `as unknown as SchemaField` cast is ever needed. `key`
  // derives from the source field or `opts.key`.
  const rel = String(join.relation);
  const o = (opts ?? {}) as Record<string, unknown>;
  const { key, labelKey, width, defaultVisible, sortable, placeholder, required, filter, filterable } =
    o as PickRefOpts;
  // Prop overrides = everything that isn't a reserved knob.
  const propsOverride: Record<string, unknown> = {};
  for (const k of Object.keys(o)) {
    if (!PICK_REF_RESERVED.has(k)) propsOverride[k] = o[k];
  }
  // When the join declares an `entity`, the same field is editable: an
  // entity-select writing the scalar FK (the part after `!` for table-hint FKs).
  // Edit filters / placeholder mirror the legacy `ref()` edit-field shape.
  const scalarFk = join.fk?.includes("!") ? join.fk.split("!")[1] : join.fk;
  let edit: Record<string, unknown> | undefined;
  if (join.entity && scalarFk) {
    const editField: Record<string, unknown> = { type: "entity-select", kind: join.entity };
    if (placeholder) editField.placeholder = placeholder;
    if (filter) {
      for (const [k, v] of Object.entries(filter)) {
        if (v !== undefined) editField[k] = v;
      }
    }
    edit = { key: scalarFk, field: editField, ...(required ? { required: true } : {}) };
  }
  return {
    // Column key defaults to the relation name (the projection's identity, and
    // what the legacy `ref()` used as `via`) — not the target field's key, which
    // would collide when several relations project the same chip field.
    key: key ?? String(join.relation),
    labelKey: labelKey ?? field.labelKey,
    label: field.label,
    component: field.component,
    render: field.render,
    props: { ...field.props, ...(propsOverride as Partial<P>) },
    data: (parent: TParent) => {
      const child =
        (parent as Record<string, unknown> | null | undefined)?.[rel] ??
        join.fallback?.(parent) ??
        null;
      // Guard null: the target field's `data` (e.g. a chip mapper) assumes a
      // present relation. When the FK didn't resolve, emit empty props rather
      // than crashing — mirrors the legacy `ref()` `child ? … : {}` behavior.
      return child != null && field.data ? field.data(child) : ({} as Partial<P>);
    },
    deps: foldHopDeps(rel, join.fk, join.also ?? [], field.deps),
    ...(width !== undefined ? { width } : {}),
    ...(defaultVisible !== undefined ? { defaultVisible } : {}),
    ...(sortable !== undefined ? { sortable } : {}),
    ...(filterable ? { filterable } : {}),
    ...(edit ? { edit: edit as unknown as FieldDef<TParent, P>["edit"] } : {}),
  } as ListColumnSpec<TParent, P>;
}

